// Настройки → Бэкапы: последний удачный бэкап, размер, статус S3, «сделать сейчас», история.
// Неудача — красным; push «сбой бэкапа» шлёт сервер.

import { errorMessage } from '@/api/client'
import { useAuthState } from '@/api/auth'
import { Badge, Button, Card, EmptyState, ErrorText, PageSpinner, useToast } from '@/components/ui'
import { formatDateTime } from '@/lib/format'

import { useBackupStatus, useRunBackup, type BackupStatus } from './api'
import { formatBytes, runStatusText, s3State } from './logic'
import { Navigate } from 'react-router-dom'

function SummaryCard({ status }: { status: BackupStatus }) {
  const run = useRunBackup()
  const toast = useToast()
  const last = status.last_run
  const success = status.last_success
  const s3 = s3State(status.s3_configured, last)
  const failed = last !== null && !last.ok

  return (
    <Card
      title="Резервные копии"
      subtitle={`Каждую ночь в 03:30 — дамп базы. На сервере хранятся ${status.keep_local} последних${
        status.s3_configured ? `, в S3 — ${status.keep_s3}` : ''
      }.`}
      actions={<Badge tone={s3.tone}>{s3.text}</Badge>}
    >
      {failed ? (
        <div role="alert" className="mb-4 rounded-lg border border-danger bg-danger-soft px-3 py-2 text-sm text-danger">
          <div className="font-medium">Последний бэкап {formatDateTime(last.started_at)}: {runStatusText(last)}</div>
          {last.error ? <div className="mt-0.5">{last.error}</div> : null}
        </div>
      ) : null}
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="text-muted">Последний удачный</dt>
        <dd className="tabular-nums text-fg">{success ? formatDateTime(success.finished_at ?? success.started_at) : 'ещё не было'}</dd>
        <dt className="text-muted">Размер</dt>
        <dd className="tabular-nums text-fg">{formatBytes(success?.size_bytes)}</dd>
        <dt className="text-muted">Копий на сервере</dt>
        <dd className="tabular-nums text-fg">{status.local_count}</dd>
        <dt className="text-muted">S3</dt>
        <dd className={s3.tone === 'danger' ? 'text-danger' : 'text-fg'}>
          {status.s3_configured ? s3.text : 'S3 не настроен — копии только на сервере. Заполни S3_* в .env, чтобы включить.'}
        </dd>
      </dl>
      <div className="mt-4">
        <Button
          variant="primary"
          icon="refresh"
          loading={run.isPending}
          onClick={() =>
            run.mutate(undefined, {
              onSuccess: (res) =>
                toast.show(
                  res.ok
                    ? { message: `Бэкап готов: ${formatBytes(res.size_bytes)}`, tone: 'accent' }
                    : { message: res.error ?? 'Бэкап не удался', tone: 'danger' },
                ),
              onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }),
            })
          }
        >
          Сделать сейчас
        </Button>
      </div>
      <p className="mt-4 text-sm text-muted">Восстановление — командой pg_restore на сервере, порядок действий описан в README.</p>
    </Card>
  )
}

function HistoryCard({ status }: { status: BackupStatus }) {
  return (
    <Card title="История">
      {status.runs.length === 0 ? (
        <EmptyState icon="history" title="Бэкапов ещё не было" description="Первый появится ночью или по кнопке «Сделать сейчас»." />
      ) : (
        <ul className="divide-y divide-border">
          {status.runs.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
              <span className="tabular-nums text-fg">{formatDateTime(r.started_at)}</span>
              <span className="tabular-nums text-muted">{formatBytes(r.size_bytes)}</span>
              <Badge tone={r.ok ? 'neutral' : 'danger'}>{runStatusText(r)}</Badge>
              {r.error ? <span className={r.ok ? 'basis-full text-muted' : 'basis-full text-danger'}>{r.error}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

export function BackupsPage() {
  const auth = useAuthState()
  if (auth.data?.user && auth.data.user.role !== 'admin') return <Navigate to="/" replace />
  return <BackupsAdminPage />
}

function BackupsAdminPage() {
  const status = useBackupStatus()
  if (status.isPending) return <PageSpinner />
  if (status.isError) return <ErrorText error={status.error} />
  return (
    <div className="grid max-w-3xl gap-4">
      <SummaryCard status={status.data} />
      <HistoryCard status={status.data} />
    </div>
  )
}
